import { useActionHandler } from "~vue/composables/useActionHandler";
import { i18n } from "~vue/i18n";
import type { ParseActionsProactiveCoachingOfferAction, ParseActionsProactiveFeatureOfferAction, UpdateActionState } from "~vue/types/chat";
import { openUrl } from "~vue/utils";
import { logError } from "~vue/utils/logUtils";
import { computed, inject, ref } from "vue";

type OfferAction = ParseActionsProactiveCoachingOfferAction | ParseActionsProactiveFeatureOfferAction;
type Feedback = "up" | "down";
type SavedState = Pick<OfferAction["action_params"], "saved_at" | "unsaved_at">;
type ActionResult = { success: boolean; payload?: SavedState & { coaching_session_url?: string; url?: string; feedback?: Feedback | null; error?: string } };
const genericErrorMessage = () => i18n.global.t("chat.proactiveOffer.genericError");

function utcTimestampKey(timestamp: string) {
    // Date.parse drops microseconds; the API emits UTC timestamps with up to six fractional digits.
    const [seconds, fraction = ""] = timestamp.replace(/Z$/, "").split(".");
    return `${seconds}.${fraction.padEnd(6, "0")}`;
}

export function useProactiveOfferAction(data: () => OfferAction, messageId: number, lineIdx: number) {
    const { execute } = useActionHandler();
    const updateActionState = inject<UpdateActionState>("updateActionState")!;
    const isLoading = ref(false);
    const isFeedbackLoading = ref(false);
    const isSaveLoading = ref(false);
    const errorMessage = ref("");
    const currentActionState = computed(() => data().action_state ?? { dismissed: false, minimized: false, submitted: false });
    const isVisible = computed(() => !currentActionState.value.dismissed && !currentActionState.value.submitted);
    const currentFeedback = computed(() => data().action_params.feedback ?? null);
    const isSaved = computed(() => {
        const { saved_at, unsaved_at } = data().action_params;
        return !!saved_at && (!unsaved_at || utcTimestampKey(saved_at) > utcTimestampKey(unsaved_at));
    });
    const isBusy = computed(() => isLoading.value || isFeedbackLoading.value || isSaveLoading.value);

    async function handleDismiss() {
        if (isBusy.value) {
            return;
        }
        const previousActionState = { ...currentActionState.value };
        isLoading.value = true;
        errorMessage.value = "";
        try {
            const result: ActionResult = await execute(
                data().action_name,
                { message_id: messageId, lineIdx, action_state: { ...previousActionState, dismissed: true } },
                { sync: true },
            );
            if (result?.success) {
                return;
            }
        } catch (error) {
            logError(error);
        } finally {
            isLoading.value = false;
        }
        updateActionState({ message_id: messageId, lineIdx, action_state: previousActionState });
        errorMessage.value = genericErrorMessage();
    }

    async function runCta(subaction: "start_chat" | "open_feature", navigate: (destination: string) => boolean) {
        if (isBusy.value) {
            return;
        }
        isLoading.value = true;
        errorMessage.value = "";
        try {
            const result: ActionResult = await execute(data().action_name, { message_id: messageId, lineIdx, subaction }, { sync: true, optimistic: false });
            const destination = subaction === "start_chat" ? result?.payload?.coaching_session_url : result?.payload?.url;
            if (!result?.success || !destination || !navigate(destination)) {
                errorMessage.value = genericErrorMessage();
                return;
            }
            updateActionState({ message_id: messageId, lineIdx, action_state: { ...currentActionState.value, submitted: true } });
        } catch (error) {
            logError(error);
            errorMessage.value = genericErrorMessage();
        } finally {
            isLoading.value = false;
        }
    }

    function handleStartChat() {
        return runCta("start_chat", (destination) => {
            if (!destination.startsWith("/")) {
                return false;
            }
            openUrl(destination);
            return true;
        });
    }

    /** Reserve the tab during the click; opening it after the async request can trigger popup blocking. */
    async function handleOpenFeature() {
        if (isBusy.value) {
            return;
        }
        let tab: Window | null = null;
        let navigated = false;
        try {
            tab = globalThis.open("", "_blank");
            if (!tab) {
                errorMessage.value = i18n.global.t("chat.proactiveOffer.popupsBlocked");
                return;
            }
            tab.opener = null;
            const destinationTab = tab;
            await runCta("open_feature", (destination) => {
                destinationTab.location.replace(destination.startsWith("/") ? new URL(destination, globalThis.location.origin).href : destination);
                navigated = true;
                return true;
            });
        } catch (error) {
            logError(error);
            errorMessage.value = genericErrorMessage();
        } finally {
            if (tab && !navigated) {
                tab.close();
            }
        }
    }

    async function handleFeedback(feedback: Feedback) {
        if (isBusy.value) {
            return;
        }
        const previousFeedback = currentFeedback.value;
        isFeedbackLoading.value = true;
        errorMessage.value = "";
        try {
            const result: ActionResult = await execute(
                data().action_name,
                { message_id: messageId, lineIdx, subaction: "rate", action_params: { feedback: previousFeedback === feedback ? null : feedback } },
                { sync: true, optimistic: false },
            );
            if (!result?.success) {
                errorMessage.value = genericErrorMessage();
                return;
            }
            updateActionState({ message_id: messageId, lineIdx, action_params: { feedback: result.payload?.feedback ?? null } });
        } catch (error) {
            logError(error);
            errorMessage.value = genericErrorMessage();
        } finally {
            isFeedbackLoading.value = false;
        }
    }

    async function handleToggleSaved() {
        if (isBusy.value) {
            return;
        }
        isSaveLoading.value = true;
        errorMessage.value = "";
        try {
            const result: ActionResult = await execute(
                data().action_name,
                { message_id: messageId, lineIdx, subaction: isSaved.value ? "unsave_for_later" : "save_for_later" },
                { sync: true, optimistic: false },
            );
            if (result?.success && result.payload && "saved_at" in result.payload) {
                const { saved_at, unsaved_at } = result.payload;
                updateActionState({ message_id: messageId, lineIdx, action_params: { saved_at, unsaved_at } });
                return;
            }
        } catch (error) {
            logError(error);
        } finally {
            isSaveLoading.value = false;
        }
        errorMessage.value = genericErrorMessage();
    }

    return { isVisible, isLoading, isBusy, currentFeedback, isSaved, errorMessage, handleDismiss, handleStartChat, handleOpenFeature, handleFeedback, handleToggleSaved };
}
